import { useState } from 'react';

import PropTypes from 'prop-types';
import { z } from 'zod';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import FormField from '#globalComponents/Forms/FormField';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { getErrorMessage } from '#lib/errors/errorMessages';

import { useCreateApiKey } from '../../models/apiKeys/mutations';

import { API_SCOPE_OPTIONS } from './constants';
import RevealSecret from './RevealSecret';

const schema = z.object({
	name: z.string().trim().min(1, 'Bitte einen Namen angeben').max(100, 'Name ist zu lang'),
	scopes: z.array(z.string()).min(1, 'Bitte mindestens eine Berechtigung wählen'),
});

const CreateApiKeyModal = ({ isOpen, onClose }) => {
	const createKey = useCreateApiKey();
	const [name, setName] = useState('');
	const [scopes, setScopes] = useState([]);
	const [formError, setFormError] = useState('');
	const [createdKey, setCreatedKey] = useState(null);

	const reset = () => {
		setName('');
		setScopes([]);
		setFormError('');
		setCreatedKey(null);
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	const toggleScope = (value, checked) => {
		setScopes((prev) => (checked ? [...prev, value] : prev.filter((s) => s !== value)));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const parsed = schema.safeParse({ name, scopes });
		if (!parsed.success) {
			setFormError(parsed.error.issues[0]?.message ?? 'Ungültige Eingabe');
			return;
		}
		setFormError('');
		try {
			const { apiKey } = await createKey.mutateAsync(parsed.data);
			setCreatedKey(apiKey);
		} catch (err) {
			setFormError(getErrorMessage(err));
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			size="m"
			as={createdKey ? undefined : 'form'}
			onSubmit={createdKey ? undefined : handleSubmit}
		>
			<Panel.Header onClose={handleClose}>
				{createdKey ? 'API-Schlüssel erstellt' : 'Neuen API-Schlüssel erstellen'}
			</Panel.Header>

			{createdKey ? (
				<Panel.Body>
					<div className="space-y-4">
						<p className="text-sm text-text">
							Kopieren Sie den Schlüssel jetzt — er wird aus Sicherheitsgründen nicht erneut angezeigt.
						</p>
						<RevealSecret
							secret={createdKey.plaintext}
							warning="Dieser Schlüssel wird nur einmal angezeigt und kann danach nicht mehr abgerufen werden."
						/>
					</div>
				</Panel.Body>
			) : (
				<Panel.Body>
					<div className="space-y-4">
						<FormField label="Name" htmlFor="apiKeyName">
							<TextInput
								id="apiKeyName"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="z. B. Zapier-Integration"
							/>
						</FormField>

						<FormField label="Berechtigungen">
							<div className="space-y-2">
								{API_SCOPE_OPTIONS.map((opt) => (
									<Checkbox
										key={opt.value}
										id={`scope-${opt.value}`}
										label={opt.label}
										checked={scopes.includes(opt.value)}
										onChange={(checked) => toggleScope(opt.value, checked)}
									/>
								))}
							</div>
						</FormField>

						{formError && <p className="text-sm text-warning-700">{formError}</p>}
					</div>
				</Panel.Body>
			)}

			<Panel.Footer className="justify-end">
				{createdKey ? (
					<Button variant="solid" onClick={handleClose}>
						Fertig
					</Button>
				) : (
					<>
						<Button variant="outline" onClick={handleClose} disabled={createKey.isPending}>
							Abbrechen
						</Button>
						<Button
							variant="solid"
							type="submit"
							loading={createKey.isPending}
							disabled={createKey.isPending}
						>
							Erstellen
						</Button>
					</>
				)}
			</Panel.Footer>
		</Modal>
	);
};

CreateApiKeyModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default CreateApiKeyModal;
